<template>
  <el-dialog
    :model-value="visible"
    :close-on-click-modal="false"
    :align-center="isPC"
    :show-close="false"
    :append-to-body="true"
    :z-index="5000"
    :width="isPC ? '90vw' : '100vw'"
    :class="[
      'editor-preview-dialog',
      {
        'editor-preview-dialog--mobile': !isPC,
        'editor-preview-dialog--forced-landscape': shouldRotateReader
      }
    ]"
    :data-reader-rotation="shouldRotateReader ? '90' : undefined"
    @update:model-value="emit('close')"
    @opened="refreshPreview"
    @closed="onDialogClosed"
  >
    <template #header>
      <div class="editor-preview-header">
        <span class="editor-preview-title">{{ $t('editor.preview') }}</span>
        <div class="editor-preview-actions">
          <!-- 移动端不显示「在手机上查看」 -->
          <PreviewMobileQrPanel
            v-if="isPC"
            v-model:visible="mobilePanelVisible"
            :label="$t('editor.viewOnMobile')"
            :qr-url="shareQrUrl.value"
          />
          <button
            type="button"
            class="editor-preview-action editor-preview-action--solid"
            @click="openShare($event)"
          >
            <SvgIcon name="book-share" :size="16" />
            {{ $t('editor.share') }}
          </button>
          <button
            type="button"
            class="editor-preview-close"
            :aria-label="$t('editor.closePreview')"
            @click="emit('close')"
          >
            <SvgIcon name="close-line" :size="20" />
          </button>
        </div>
      </div>
    </template>

    <div class="editor-preview-body" :class="{ 'editor-preview-body--mobile': !isPC }">
      <BookReader v-if="bookReaderMounted" ref="bookReaderRef" embedded />
    </div>
  </el-dialog>
</template>

<script setup lang="ts">
  import { computed, ref, watch } from 'vue';
  import SvgIcon from '@/components/common/icon/SvgIcon.vue';
  import BookReader from '@/views/book/components/BookReader.vue';
  import PreviewMobileQrPanel from './PreviewMobileQrPanel.vue';
  import { useProjectStore } from '@/store/project.ts';
  import { storeToRefs } from 'pinia';
  import { useShareQrcode } from '@/hooks/useShareQrcode.ts';
  import { useDebugGuard } from '@/hooks/useDebugGuard';
  import { useReaderLandscape } from '@/hooks/reader/useReaderLandscape';

  const props = defineProps<{
    visible: boolean;
  }>();

  const emit = defineEmits<{
    close: [];
  }>();

  const bookReaderRef = ref<InstanceType<typeof BookReader> | null>(null);
  const mobilePanelVisible = ref(false);
  /** 控制 BookReader 的挂载/卸载，弹窗关闭动画结束后设为 false 以销毁 PixiJS Application */
  const bookReaderMounted = ref(false);

  // 预览弹窗仅在正式环境(pro)禁用右键菜单与调试快捷键；dev/test 可正常调试。
  const { enable: enableDebugGuard, disable: disableDebugGuard } = useDebugGuard();
  /** 是否为 PC 端（移动端隐藏「在手机上查看」，并用非全屏大弹窗适配顶栏/浏览器栏） */
  const { isPC, shouldRotateReader } = useReaderLandscape();

  const { shareUrl } = storeToRefs(useProjectStore());
  const shareQrUrl = computed(() => {
    return useShareQrcode(shareUrl.value).dataUrl;
  });

  /** 打开分享面板，并把分享按钮位置传给阅读器用于定位。 */
  const openShare = (e: MouseEvent) => {
    mobilePanelVisible.value = false;
    const rect = (e.currentTarget as HTMLElement)?.getBoundingClientRect();
    bookReaderRef.value?.shareBook(rect);
  };

  /**
   * Element Plus 首次打开 Dialog 时才渲染默认插槽。
   * 等 opened 后 BookReader ref 与内部 Canvas 都已挂载，再重建书本并触发封面启动音频。
   */
  const refreshPreview = async () => {
    if (!props.visible || !bookReaderRef.value) return;
    await bookReaderRef.value.refresh();
  };

  watch(
    () => props.visible,
    (visible) => {
      if (!visible) {
        // 关闭时停止播放并销毁书本/时间线（弹窗仅隐藏，需主动清理）
        bookReaderRef.value?.stopAutoplay();
        bookReaderRef.value?.destroy();
        mobilePanelVisible.value = false;
        disableDebugGuard();
        return;
      }

      // 打开时挂载 BookReader（若之前已被 onDialogClosed 卸载）
      bookReaderMounted.value = true;
      enableDebugGuard();
    }
  );

  /** 弹窗关闭动画结束后，卸载 BookReader 以销毁其 PixiJS Application，
   *  确保页面上只保留编辑器的 Application，避免多 Application 资源竞态。 */
  function onDialogClosed() {
    bookReaderMounted.value = false;
  }
</script>

<style>
  /* el-dialog is teleported to <body>, so Vue's scoped data-v attribute never
     lands on its root — these overrides must live in an unscoped block. */
  .editor-preview-dialog.el-dialog {
    padding: 10px;
    max-width: 1600px;
    background: rgba(239, 240, 247, 0.9);
    border: 1px solid rgba(60, 60, 60, 0.08);
    border-radius: 12px;
    backdrop-filter: blur(60px);
    -webkit-backdrop-filter: blur(60px);
  }

  .editor-preview-dialog.el-dialog .el-dialog__header {
    padding: 0;
    margin: 0;
  }

  .editor-preview-dialog.el-dialog .el-dialog__body {
    padding: 0;
  }

  /*
   * 移动端：不用 EP fullscreen，用 CSS 让弹窗等于视口（100vw×100dvh）。
   * 高 z-index 盖住工作台顶栏；不留 overlay padding，避免「小窗口」观感。
   */
  /* 步骤1：遮罩铺满，禁止滚动 */
  .el-overlay-dialog:has(.editor-preview-dialog--mobile) {
    overflow: hidden;
    padding: 0;
  }

  /* 步骤2：弹窗与视口同大 */
  .editor-preview-dialog--mobile.el-dialog {
    position: fixed !important;
    inset: 0 !important;
    width: 100vw !important;
    max-width: none !important;
    height: 100% !important;
    height: 100dvh !important;
    max-height: none !important;
    margin: 0 !important;
    padding: 0 !important;
    border: 0 !important;
    border-radius: 0 !important;
    display: flex !important;
    flex-direction: column;
    overflow: hidden;
    box-sizing: border-box;
  }

  .editor-preview-dialog--forced-landscape.el-dialog {
    inset: auto !important;
    top: 50% !important;
    left: 50% !important;
    width: 100vh !important;
    width: 100dvh !important;
    height: 100vw !important;
    transform: translate(-50%, -50%) rotate(90deg);
    transform-origin: center;
  }

  /* 步骤3：header 固定，body 吃满剩余高度 */
  .editor-preview-dialog--mobile.el-dialog .el-dialog__header {
    flex-shrink: 0;
    padding: 0 12px;
  }

  .editor-preview-dialog--mobile.el-dialog .el-dialog__body {
    flex: 1 1 auto;
    min-height: 0;
    height: 0;
    overflow: hidden;
    display: flex;
    flex-direction: column;
  }
</style>

<style scoped>
  .editor-preview-dialog {
    user-select: none;
  }

  .editor-preview-header {
    display: flex;
    align-items: center;
    padding: 3px 0;
    justify-content: space-between;
    height: auto;
    min-height: 32px;
    box-sizing: border-box;
  }

  .editor-preview-title {
    color: #160211;
    font-size: 15px;
    font-weight: 500;
    font-family: 'Microsoft YaHei', sans-serif;
  }

  .editor-preview-actions {
    display: flex;
    align-items: center;
    gap: 11px;
  }

  .editor-preview-action {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    height: 32px;
    padding: 0 16px;
    color: #160211;
    border: 0;
    border-radius: 20px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
    transition: background-color 160ms ease;
  }

  .editor-preview-action--ghost {
    background: rgba(255, 255, 255, 0.15);
  }

  .editor-preview-action--ghost:hover {
    background: rgba(255, 255, 255, 0.25);
  }

  .editor-preview-action--solid {
    background: #fbf9ff;
  }

  .editor-preview-action--solid:hover {
    background: #fff;
  }

  .editor-preview-close {
    width: 32px;
    height: 32px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    color: #160211;
    background: transparent;
    border: 0;
    border-radius: 8px;
    cursor: pointer;
    transition: background-color 160ms ease;
  }

  .editor-preview-close:hover {
    background: rgba(22, 2, 17, 0.06);
  }

  .editor-preview-body {
    margin: 10px 0 0 0 !important;
    height: 80vh;
    display: flex;
    overflow: hidden;
    border-radius: 6px;
    flex-direction: column;
  }

  /* 移动端：覆盖 80vh，随 dialog body 撑满视口剩余高度 */
  .editor-preview-body--mobile {
    flex: 1 1 auto;
    min-height: 0;
    height: 100%;
    padding: 0;
  }
</style>
